/**
 *
 * Most modern mobile touch slider and framework with hardware accelerated transitions
 * http://146.56.218.109:18086/wechat/index.html
 *
 * Copyright 2024-2031 Vladimir Kharlampidi
 *
 * Released under the MIT License
 *
 */
document.addEventListener("DOMContentLoaded", function () {
	/**
	 * 初始化滚动动画 - 可配置的滚动动画（支持方向检测，优化缩放环境）
	 *
	 * @param {string} selector - 选择器，默认为 ".sp-opacity-0"
	 * @param {object} options - 配置选项
	 * @param {number} options.threshold - 可见阈值，默认 0.2（20%）
	 * @param {number} options.duration - 动画持续时间（秒），默认 1
	 * @param {string} options.easing - 动画缓动函数，默认 "ease-out"（更适合缩放环境）
	 * @param {string} options.animationType - 动画类型："fade"（淡入，推荐），"fade-scale"（缩放+淡入），"fade-translate"（平移+淡入）
	 *
	 * 使用方法：
	 * 1. 每次进入可视区域都播放（默认）：
	 *    <div class="sp-opacity-0">内容</div>
	 *
	 * 2. 只触发一次：
	 *    <div class="sp-opacity-0" data-animation-repeat="false">内容</div>
	 *
	 * 3. 指定动画类型：
	 *    <div class="sp-opacity-0" data-animation-type="fade-scale">内容</div>
	 *
	 * 动画效果（针对缩放环境优化）：
	 * - fade: 纯淡入动画（最稳定，推荐用于缩放环境）
	 * - fade-scale: 缩放+淡入动画（视觉效果更好）
	 * - fade-translate: 平移+淡入动画（使用相对单位vh，自适应缩放）
	 */
	function createScrollAnimation(selector = ".sp-opacity-0", options = {}) {
		const {
			threshold = 0.2,
			duration = 1,
			easing = "ease-out", // 使用 ease-out 在缩放环境下更流畅
			animationType = "fade-translate", // 默认使用相对单位的平移动画
		} = options;

		const animateItems = document.querySelectorAll(selector);

		if (!animateItems.length) {
			// console.warn(`createScrollAnimation: 未找到选择器 "${selector}" 对应的元素`);
			return;
		}

		// 记录上一次的滚动位置，用于判断滚动方向
		let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
		let scrollDirection = "down"; // 默认向下

		// 监听滚动事件，更新滚动方向
		const handleScroll = () => {
			const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
			scrollDirection = currentScrollTop > lastScrollTop ? "down" : "up";
			lastScrollTop = currentScrollTop;
		};

		window.addEventListener("scroll", handleScroll, { passive: true }); // 使用 passive 提升性能

		const observerAni = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const element = entry.target;
					// 检查是否配置为重复播放，默认为 false（只触发一次，避免反复触发）
					// 只有显式设置为 "true" 时才重复
					const repeatAttr = element.getAttribute("data-animation-repeat");
					const isRepeat = repeatAttr === "true"; // 默认 false，只有显式设置为 "true" 才重复
					// 检查元素是否正在动画中
					const isAnimating = element.getAttribute("data-is-animating") === "true";
					// 获取元素指定的动画类型，或使用默认值
					const elementAnimationType = element.getAttribute("data-animation-type") || animationType;

					if (entry.isIntersecting) {
						// 对于重复模式，如果元素正在动画中，不重复触发（避免动画冲突）
						// 对于非重复模式，如果已经动画过，也不触发（由 unobserve 处理）
						if (isAnimating) {
							return;
						}

						// 标记为正在动画中
						element.setAttribute("data-is-animating", "true");

						// 根据动画类型和滚动方向选择不同的动画
						let animationName;
						if (elementAnimationType === "fade") {
							// 纯淡入，不受滚动方向影响
							animationName = "sp-fade-only";
						} else if (elementAnimationType === "fade-scale") {
							// 缩放+淡入
							animationName = scrollDirection === "down" ? "sp-fade-scale-in" : "sp-fade-scale-up";
						} else {
							// 默认：平移+淡入（使用相对单位）
							animationName = scrollDirection === "down" ? "sp-fade-in" : "sp-fade-up";
						}

						// 清除之前的动画和内联样式，让动画从 keyframes 定义的初始状态开始
						// 注意：CSS 动画会覆盖内联样式，所以这里主要是确保动画能够重新触发
						element.style.animation = "none";
						// 清除之前设置的内联 transform 和 opacity（让动画从 keyframes 的 0% 开始）
						// 但保留 translateZ(0) 以保持硬件加速（通过 CSS 类设置）
						element.style.transform = "";
						element.style.opacity = "";
						void element.offsetWidth; // 强制重排，确保样式被清除

						// 应用新动画（动画会从 keyframes 的 0% 状态开始，覆盖任何现有样式）
						element.style.animation = `${animationName} ${duration}s ${easing} forwards`;

						// 动画结束后移除标记
						const handleAnimationEnd = () => {
							// 检查元素是否仍在可视区域（快速滚动可能导致已经离开）
							const rect = element.getBoundingClientRect();
							const isStillVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;

							element.setAttribute("data-is-animating", "false");
							element.removeEventListener("animationend", handleAnimationEnd);

							// 如果动画结束时元素仍在可视区域，移除 will-change 以提升性能
							if (isStillVisible) {
								element.style.willChange = "auto";
							}
						};
						element.addEventListener("animationend", handleAnimationEnd);

						// 如果不是重复模式，播放一次后停止观察
						if (!isRepeat) {
							observerAni.unobserve(element);
						}
					} else {
						// 元素离开可视区域
						if (isRepeat) {
							// 重复模式：无论是否在动画中，都立即重置以便下次播放

							// 停止当前动画并移除动画事件监听器
							element.style.animation = "none";

							// 立即重置 isAnimating 标记，以便下次进入可视区域时可以触发
							// 这是关键修复：即使动画还没结束，也要重置标记
							element.setAttribute("data-is-animating", "false");

							// 根据动画类型重置到初始状态
							// 获取元素使用的动画类型
							const resetAnimationType = element.getAttribute("data-animation-type") || animationType;

							// 判断元素是从哪个方向离开的（用于决定重置时的初始位置）
							// 通过检查元素的 boundingClientRect 位置来判断
							const rect = entry.boundingClientRect;
							const isAboveViewport = rect.bottom < 0; // 元素在视口上方

							// 确定重置时的初始方向（默认从下方进入，如果从上方离开则从上方进入）
							const resetDirection = isAboveViewport ? "up" : "down";

							// 重置 transform 和 opacity 到动画的初始状态
							if (resetAnimationType === "fade") {
								// 纯淡入：只需要重置 opacity，不依赖方向
								element.style.opacity = "0";
								element.style.transform = "translateZ(0)"; // 保持硬件加速
							} else if (resetAnimationType === "fade-scale") {
								// 缩放+淡入：重置 scale、translateY 和 opacity
								element.style.opacity = "0";
								const initialTranslateY = resetDirection === "down" ? "5vh" : "-5vh";
								element.style.transform = `scale(0.9) translateY(${initialTranslateY}) translateZ(0)`;
							} else {
								// fade-translate：重置 translateY 和 opacity
								element.style.opacity = "0";
								const initialTranslateY = resetDirection === "down" ? "10vh" : "-10vh";
								element.style.transform = `translateY(${initialTranslateY}) translateZ(0)`;
							}

							// 强制浏览器重排，确保样式被应用
							void element.offsetWidth;
							// 恢复 will-change，准备下次动画
							element.style.willChange = "transform, opacity";
						}
					}
				});
			},
			{
				threshold,
				// 添加 rootMargin 提供缓冲区，避免边缘频繁触发
				// 减小缓冲区以降低触发敏感度
				rootMargin: "20px 0px 20px 0px",
			}
		);

		// 观察每个元素
		animateItems.forEach((item) => {
			observerAni.observe(item);
		});

		// 返回清理函数，用于取消监听和观察
		return function cleanup() {
			window.removeEventListener("scroll", handleScroll);
			animateItems.forEach((item) => {
				observerAni.unobserve(item);
			});
		};
	}
	// 初始化滚动动画（针对缩放环境优化）
	createScrollAnimation(".sp-opacity-0", {
		threshold: 0.2,
		duration: 0.5,
		easing: "ease-out", // 在缩放环境下使用 ease-out 更流畅
		animationType: "fade-translate", // 使用相对单位的平移动画，自适应缩放
	});
	// 倒计时
	function getRandomNum(min, max) {
		return Math.floor(Math.random() * (max - min)) + min;
	}

	let endTimeG;

	function cutDownTime() {
		let nowTime = new Date();
		let endTime = new Date(endTimeG);

		let leftTime = endTime.getTime() - nowTime.getTime();
		if (leftTime < 0) {
			setupNewCountdown();
			leftTime = 0;
		}
		let leftH = Math.floor((leftTime / (1000 * 60 * 60)) % 24),
			leftM = Math.floor((leftTime / (1000 * 60)) % 60),
			leftS = Math.floor((leftTime / 1000) % 60);
		leftH = leftH >= 10 ? leftH : "0" + leftH;
		leftM = leftM >= 10 ? leftM : "0" + leftM;
		leftS = leftS >= 10 ? leftS : "0" + leftS;

		return {
			hour: leftH,
			minute: leftM,
			second: leftS,
		};
	}
	function setupNewCountdown() {
		const randomHour = getRandomNum(1, 9);
		const currentTime = new Date();
		endTimeG = currentTime.setHours(currentTime.getHours() + randomHour);
	}
	let intervalId;

	function getCutDownTime() {
		setupNewCountdown();
		if (intervalId) clearInterval(intervalId);
		intervalId = setInterval(function () {
			let cutDownTimeText = cutDownTime();
			$(".sp_top_cut_down .sp_left_view .sp_hour").html(cutDownTimeText.hour);
			$(".sp_top_cut_down .sp_left_view .sp_minute").html(cutDownTimeText.minute);
			$(".sp_top_cut_down .sp_left_view .sp_second").html(cutDownTimeText.second);
		}, 1000);
	}

	getCutDownTime();

	new WOW().init();

	let spImgList = [
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-p1.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-p2.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-p3.png",

		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-a1.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-a2.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-a3.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-a4.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-a5.png",

		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-b1.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-b2.webp",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-b3.webp",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-b4.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-b5.png",

		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c1.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c2.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c3.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c4.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c5.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c6.png",
		"https://cdn.shopify.com/s/files/1/0791/0100/2012/files/20251030-c7.png",
	];

	new Swiper(".sp-swiper-fade", {
		spaceBetween: 10,
		effect: "fade",
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
			delay: 2000,
		},
		speed: 2000,
	});

	new Swiper(".sp-swiper-fade1", {
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
		},
		spaceBetween: 10,
		loop: true,
		on: {
			slideChange: function (e) {
				e.slides.find(".sp-text").each(function () {
					this.style.animation = "none";
				});
				const spText = e.slides.eq(e.activeIndex).find(".sp-text")[0];
				if (spText) {
					spText.style.animation = "none";

					spText.style.animation = `swiper-up-fade 0.5s ease forwards`;
				}
			},
		},
	});

	new Swiper(".sp-swiper0", {
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
		},
		spaceBetween: 10,
		loop: true,
		pagination: {
			el: ".sp-swiper-fadeagination1",
			clickable: true,
		},
	});

	new Swiper(".sp-swiper-scroll2", {
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
			delay: 0,
		},
		slidesPerView: "auto",
		speed: 5000,
		loop: true,
		spaceBetween: 15,
	});

	new Swiper(".sp-swiper-scroll2-pc", {
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
			delay: 0,
		},
		slidesPerView: "auto",
		speed: 5000,
		loop: true,
		spaceBetween: 15,
	});
	new Swiper(".sp-swiper-right", {
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
		},
		spaceBetween: 20,
		loop: true,
		speed: 2000,
		pagination: {
			el: ".swiper-pagination",
			clickable: true,
		},
	});

	new Swiper(".sp-swiper1", {
		autoplay: true,
		autoplay: {
			disableOnInteraction: false,
		},
		spaceBetween: 0,
		loop: true,
	});

	new Swiper(".sp-swiper-box-mobile", {
		slidesPerView: 1,
		centeredSlides: true,
		spaceBetween: 30,
		loop: true,
		autoplay: {
			delay: 3000,
			disableOnInteraction: false,
		},
		pagination: {
			el: ".swiper-pagination",
			clickable: true,
		},
	});

	new Swiper(".sp-part-3-swiper", {
		slidesPerView: 1,
		spaceBetween: 0,
		loop: true,
		autoplay: {
			delay: 3000,
			disableOnInteraction: false,
		},
		pagination: {
			el: ".sp-part-3-pagination",
			clickable: true,
		},
	});

	// new Swiper(".sp-pc .sp-part-6-swiper", {
	// 	slidesPerView: 3,
	// 	centeredSlides: true,
	// 	spaceBetween: 30,
	// 	loop: true,
	// 	autoplay: {
	// 		delay: 3000,
	// 		disableOnInteraction: false,
	// 	},
	// });

	new Swiper(".sp-part-6-swiper-mobile", {
		slidesPerView: 'auto',
		centeredSlides: true,
		spaceBetween: 30,
		loop: true,
		autoplay: {
			delay: 3000,
			disableOnInteraction: false,
		},
	});

	$(".sp-mobile .sp-option").click(function () {
		$(this).siblings().removeClass("active");
		$(this).addClass("active");

		// 只在点击 type 选项时切换图片
		if ($(this).parent().hasClass("sp-type")) {
			let typeOptions = $(".sp-mobile .sp-type .sp-option.active").index();

			$(".product-part.sp-mobile .sp-img-p").eq(typeOptions).addClass("active").siblings().removeClass("active");
			changePrice1()
		}
	});

	$(".sp-pc .sp-option").click(function () {
		$(this).siblings().removeClass("active");
		$(this).addClass("active");

		// 只在点击 type 选项时切换图片
		if ($(this).parent().hasClass("sp-type")) {
			let typeOptions = $(".sp-pc .sp-type .sp-option.active").index();

			$(".product-part.sp-pc .sp-img-p").eq(typeOptions).addClass("active").siblings().removeClass("active");
			changePrice2()
		}
	});

	// Part5 卡片切换功能（移动端）
	let part5AutoplayInterval;

	// 切换到指定卡片的函数
	function switchPart5Card(index) {
		$(".sp-part5-mobile .card-box .card").removeClass("active");
		$(".sp-part5-mobile .card-box .card").eq(index).addClass("active");

		// 切换图片
		let imgSrc = "https://cdn.shopify.com/s/files/1/0791/0100/2012/files/part5-" + (index + 1) + ".png";
		$(".sp-part5-mobile .card-box img").attr("src", imgSrc);
	}

	// 启动自动轮播
	function startPart5Autoplay() {
		if (part5AutoplayInterval) {
			clearInterval(part5AutoplayInterval);
		}

		part5AutoplayInterval = setInterval(function () {
			let currentIndex = $(".sp-part5-mobile .card-box .card.active").index();
			let nextIndex = (currentIndex + 1) % 3; // 循环：0->1->2->0
			switchPart5Card(nextIndex);
		}, 3000); // 每3秒切换一次
	}

	// 点击卡片事件
	$(".sp-part5-mobile .card-box .card").click(function () {
		let cardIndex = $(this).index();
		switchPart5Card(cardIndex);

		// 重置自动轮播
		startPart5Autoplay();
	});

	// 启动自动轮播
	startPart5Autoplay();

	// Part6 标签切换功能（PC端）
	$(".sp-part6-pc .sp-swiper-box .left .row > div").click(function () {
		// 切换 active 类名
		$(".sp-part6-pc .sp-swiper-box .left .row > div").removeClass("active");
		$(this).addClass("active");
	});

	// Part7 标签切换功能（移动端）
	$(".sp-part7-mobile .emoji-box .row > div").click(function () {
		// 切换 active 类名
		$(".sp-part7-mobile .emoji-box .row > div").removeClass("active");
		$(this).addClass("active");
	});

	function mainRender() {
		$(function () {
			$("#root").css("zoom", "normal");
			if (document.body.clientWidth <= 1060) {
				let scale = document.body.clientWidth / 1080;
				$("#root").css("zoom", scale);
			} else {
				const scale = document.body.clientWidth / 1920;
				$("#root").css("zoom", scale);
			}
			window.addEventListener(
				"resize",
				(function () {
					var timer;
					return function () {
						if (timer) clearTimeout(timer);
						timer = setTimeout(function () {
							$("#root").css("zoom", "normal");
							if (document.body.clientWidth <= 1060) {
								let scale = document.body.clientWidth / 1080;
								$("#root").css("zoom", scale);
							} else {
								const scale = document.body.clientWidth / 1920;
								$("#root").css("zoom", scale);
							}
						}, 100);
					};
				})()
			);
		});

		function lazyLoadImages() {
			// 懒加载
			const images = document.querySelectorAll(".lazy-load"); // 获取所有需要懒加载的图片
			const config = {
				rootMargin: "0px 0px 1300px 0px",
				threshold: 0,
			};

			const lazyLoadObserver = new IntersectionObserver(function (entries, observer) {
				entries.forEach(function (entry) {
					if (entry.isIntersecting) {
						// 当图片进入视口时
						const image = entry.target;
						const dataSrc = image.getAttribute("data-src");
						image.src = dataSrc; // 将data-src属性的值设置为图片的实际地址
						lazyLoadObserver.unobserve(image); // 停止观察该图片
					}
				});
			}, config);

			images.forEach(function (image) {
				lazyLoadObserver.observe(image); // 开始观察每个图片
			});
		}

		function preloadImages(imageArray) {
			// 预加载
			// 检查数组数据结构
			if (Array.isArray(imageArray[0])) {
				// 处理二维数组
				imageArray.forEach((subArray) => {
					subArray.forEach((url) => {
						const img = new Image();
						img.src = url;
					});
				});
			} else {
				// 处理一维数组
				imageArray.forEach((url) => {
					const img = new Image();
					img.src = url;
				});
			}
		}

		lazyLoadImages();

		preloadImages(spImgList);

		// 找到所有跳转套餐的锚点链接
		var links = document.querySelectorAll('a[href^="#shopping"]');
		links.forEach(function (link) {
			link.addEventListener("click", function (event) {
				event.preventDefault(); // 阻止默认的跳转行为
				// 从链接的 href 中获取目标 ID
				const targetId = this.getAttribute("href").substring(1); // 去掉 # 号
				const targetElement = document.getElementById(targetId); // 获取跳转目标节点
				if (!targetElement) return; // 如果目标不存在则返回
				const offsetTop = targetElement.offsetTop;
				let sptop = document.body.clientWidth <= 1080 ? 399 : 94;

				window.scroll({
					top: offsetTop * $("div#root").css("zoom") - sptop * $("div#root").css("zoom"),
					behavior: "smooth", // 使用 smooth 实现平滑滚动
				});
			});
		});

		// IOS字体多了间距的处理 给节点添加 sp_system 类名
		// 判断当前用户是安卓端还是iOS端
		var userAgent = navigator.userAgent || navigator.vendor || window.opera;
		var isAndroid = /android/i.test(userAgent);
		var isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
		var isPC = !isAndroid && !isIOS && !/Iron/.test(userAgent); // 添加了对 "Iron" 关键词的判断

		if (isAndroid) {
			console.log("Android");
		} else if (isIOS) {
			console.log("iOS");
		} else if (isPC) {
			console.log("PC");
		} else {
			$(".sp_system").css("letter-spacing", "-4px");
		}
	}

	mainRender();

	// 展示尺码弹窗
	$(".sp-size-btn").click(function () {
		$(".sp-mask").css("display", "flex");
		$(".sp-mask .img-pop").show();
	});
	// 关闭弹窗按钮

	$(".sp-mask").click(function (e) {
		// if ($(e.target).closest(".sp-mask .choose-btn").length > 0) {
		// } else {
		$(".sp-mask").hide();
		// }
	});

	let sp_price1 = 59.99;
	let sp_price2 = 99.99;

	function calcTotal(price, num) {
		let result = Number(price) * Number(num);
		return result.toFixed(2);
	}
  let prods = window.product
  function getCur1() {
    let num = $('.sp-mobile #sp-type div.active').attr('data-val')
    return prods.filter(
      item => item.option1 == num 
    )[0]
  }
  function getCur2() {
    let num = $('.sp-pc #sp-type div.active').attr('data-val')
    return prods.filter(
      item => item.option1 == num 
    )[0]
  }
  function changePrice1() {
    let num = $('.sp-mobile #calcNumber').val()
    let price = ((getCur1().price * num) / 100).toFixed(2)
    let compare = ((getCur1().compare_at_price * num) / 100).toFixed(2)
    // price = (price + '').replace('.', ',')
    // compare = (compare + '').replace('.', ',')
    $('.sp-mobile #sp_price3').html(price)
    $('.sp-mobile #sp_price4').html(compare)

    let off_font =
      100 - Math.ceil((getCur1().price / getCur1().compare_at_price) * 100)
    $('.sp-mobile.product-part .price-group .off-font').html(off_font)
  }
  function changePrice2() {
    let num = $('.sp-pc #calcNumber').val()
    let price = ((getCur2().price * num) / 100).toFixed(2)
    let compare = ((getCur2().compare_at_price * num) / 100).toFixed(2)
    // price = (price + '').replace('.', ',')
    // compare = (compare + '').replace('.', ',')
    $('.sp-pc #sp_price3').html(price)
    $('.sp-pc #sp_price4').html(compare)

    let off_font =
      100 - Math.ceil((getCur2().price / getCur2().compare_at_price) * 100)
    $('.sp-pc.product-part .price-group .off-font').html(off_font)
  }
  $('.sp-mobile .sp-btn-style').click(function () {
    let num = $('.sp-mobile #calcNumber').val()
    window.location.href = '/cart/' + getCur1().id + ':' + num
  })
  $('.sp-pc .sp-btn-style').click(function () {
    let num = $('.sp-pc #calcNumber').val()
    window.location.href = '/cart/' + getCur2().id + ':' + num
  })
	$(".sp-mobile .sp-reduce").click(function () {
		let input = $(this).siblings("input")[0];
		let num = Number(input.value) - 1;
		if (num > 0) {
			input.value = num;
		}
		changePrice1()
	});
	$(".sp-mobile .sp-add").click(function () {
		let input = $(this).siblings("input")[0];
		let num = Number(input.value) + 1;
		input.value = num;
		changePrice1()
	});

	$(".sp-pc .sp-reduce").click(function () {
		let input = $(this).siblings("input")[0];
		let num = Number(input.value) - 1;
		if (num > 0) {
			input.value = num;
		}
		changePrice2()
	});
	$(".sp-pc .sp-add").click(function () {
		let input = $(this).siblings("input")[0];
		let num = Number(input.value) + 1;
		input.value = num;
		changePrice2()
	});

	// 标题文本渐出
	const graduallyList = document.getElementsByClassName("gradually");
	for (let gradually of graduallyList) {
		const spans = gradually.querySelectorAll("span");
		const paragraph = gradually.querySelector(".sp-sub-title");

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						// 给每个 span 和 i 动画
						spans.forEach((span, index) => {
							span.style.animation = `swift-up-fade 0.5s ease forwards`;
							span.style.animationDelay = `${index * 0.1}s`;

							const i = span.querySelector("i");
							if (i) {
								i.style.animation = `swift-up-fade 0.5s ease forwards`;
								i.style.animationDelay = `${index * 0.1}s`;
							}
						});

						// 段落
						const totalDelay = spans.length * 0.1 + 0.3;
						if (paragraph) {
							paragraph.style.animation = `swift-up-fade 0.5s ease forwards`;
							paragraph.style.animationDelay = `${totalDelay}s`;
						}

						observer.unobserve(gradually);
					}
				});
			},
			{
				threshold: 0.3,
			}
		);
		observer.observe(gradually);
	}

	// // 点击按钮
	// $(".sp-video-btn").click(function () {
	//   $(".sp-mask").css("display", "flex");
	// });

	// 菜单
	$(".sp-menu-btn").click(function (e) {
		$(".sp-menu-mask").show();
	});

	$(".sp-menu-mask").click(function (e) {
		// if ($(e.target).closest(".sp-menu-mask li").length > 0) {
		// } else {
		$(".sp-menu-mask").hide();
		// }
	});

	// 滚动隐藏顶部
	// if (
	//   $(window).scrollTop() >
	//   $(".sp-header").height() * $("div#root").css("zoom")
	// ) {
	//   $(".header-wrap").addClass("sp-fixed");
	// } else {
	//   $(".header-wrap").removeClass("sp-fixed");
	// }

	// // 滚动添加底部
	// if (
	//   $(window).scrollTop() >
	//   $(".sp-part1").height() * $("div#root").css("zoom") -
	//     $(".sp-part1 .swiper").height() * $("div#root").css("zoom")
	// ) {
	//   $(".sp-btn-style2").addClass("sp-fixed-bottom");
	// } else {
	//   $(".sp-btn-style2").removeClass("sp-fixed-bottom");
	// }

	// $(window).scroll(function () {
	//   if (
	//     $(window).scrollTop() >
	//     $(".sp-header").height() * $("div#root").css("zoom")
	//   ) {
	//     $(".header-wrap").addClass("sp-fixed");
	//   } else {
	//     $(".header-wrap").removeClass("sp-fixed");
	//   }

	//   // if (
	//   //   $(window).scrollTop() >
	//   //   $(".sp-part1").height() * $("div#root").css("zoom") -
	//   //     $(".sp-part1 .swiper").height() * $("div#root").css("zoom")
	//   // ) {
	//   //   $(".sp-btn-style2").addClass("sp-fixed-bottom");
	//   // } else {
	//   //   $(".sp-btn-style2").removeClass("sp-fixed-bottom");
	//   // }
	// });

	$(function () {
		// 购物车加购动效
		$(".sp-mobile .cart-btn").click(function () {
			$(this).children("img").addClass("on");
			$(".sp-mobile .sp-cart .num").show();
			setTimeout(function () {
				$(".sp-mobile .cart-btn").children("img").removeClass("on");
			}, 500);
			$(".sp-mobile .sp-cart").addClass("on");
			setTimeout(function () {
				$(".sp-mobile .sp-cart").removeClass("on");
			}, 500);
		});

		let initNum = 0;

		$(".product-part.sp-mobile .cart-btn").click(function () {
			var data = {
				id: getCur1().id,
				quantity: $('.sp-mobile #calcNumber').val(),
			}
			$.ajax({
				type: 'POST',
				url: '/cart/add.js',
				data: data,
				success: function () {
				$.getJSON(window.Shopify.routes.root + 'cart.js', function (result) {
					$('.sp-mobile #cardNum').html(result.item_count)
					$(".sp-mobile .cardNum").removeClass("sp-red");
				})
				},
				dataType: 'json',
			})
			// handleAddToCart(initNum);

			// var imgs = $(".product-part .swiper-slide-active").find("img");
			var imgs = $(".product-part.sp-mobile .sp-img-p.active");

			imgs.each(function () {
				var $img = $(this);
				var flyer = $img[0].cloneNode(true);
				flyer.classList.add("sp_clone");

				$(".product-part.sp-mobile").append(flyer);
				$(flyer).css({
					width: $img[0].width,
					height: $img[0].height,
					background: "#e7e9ef",
					position: "fixed",

					top: $img[0].offsetTop,
					left: $img.offset().left,
					"z-index": 99,
				});

				let finish = $(".sp-btn-style2.sp-mobile .sp-cart");

				$(flyer).animate(
					{
						top: $(".sp-btn-style2")[0].offsetTop,
						left: $(".sp-btn-style2 .sp-area2")[0].offsetLeft + finish.width() / 2,
						width: 50,
						height: 50,
						opacity: 0.4,
					},
					500,
					"linear",
					function () {
						$(this).animate(
							{
								width: 0,
								height: 0,
								opacity: 0,
							},
							0,
							function () {
								$(this).detach();
							}
						);
					}
				);
			});
		});
	});

	// PC
	$(function () {
		// 购物车加购动效
		$(".sp-pc .cart-btn").click(function () {
			$(this).children("img").addClass("on");
			$(".sp-pc .sp-header-top .sp-cart .num").show();
			setTimeout(function () {
				$(".sp-pc .cart-btn").children("img").removeClass("on");
			}, 500);
			$(".sp-pc .sp-header-top .sp-cart").addClass("on");
			setTimeout(function () {
				$(".sp-pc .sp-header-top .sp-cart").removeClass("on");
			}, 500);
		});

		let initNum = 0;
		$(".product-part.sp-pc .cart-btn").click(function () {
			var data = {
				id: getCur2().id,
				quantity: $('.sp-pc #calcNumber').val(),
			}
			$.ajax({
				type: 'POST',
				url: '/cart/add.js',
				data: data,
				success: function () {
				$.getJSON(window.Shopify.routes.root + 'cart.js', function (result) {
					$('.sp-pc #cardNum').html(result.item_count)
					$(".sp-pc .cardNum").removeClass("sp-red");
				})
				},
				dataType: 'json',
			})
			// handleAddToCart(initNum);

			// var imgs = $(".product-part .swiper-slide-active").find("img");
			var imgs = $(".product-part.sp-pc .sp-img-p.active");

			imgs.each(function () {
				var $img = $(this);
				var flyer = $img[0].cloneNode(true);
				flyer.classList.add("sp_clone");

				$(".product-part.sp-pc").append(flyer);
				$(flyer).css({
					width: $img[0].width,
					height: $img[0].height,
					background: "#e7e9ef",
					position: "fixed",

					top: $img[0].offsetTop,
					left: $img.offset().left,
					"z-index": 99,
				});

				let finish = $(".sp-pc .sp-cart");
				$(flyer).animate(
					{
						top: finish[0].offsetTop,
						left: finish[0].offsetLeft + finish.width() / 2,
						width: 50,
						height: 50,
						opacity: 0.4,
					},
					500,
					"linear",
					function () {
						$(this).animate(
							{
								width: 0,
								height: 0,
								opacity: 0,
							},
							0,
							function () {
								$(this).detach();
							}
						);
					}
				);
			});
		});
	});

	// 问答
	// 点击标题区域 - 正常展开/关闭
	$(".q-row").click(function () {
		const currentItem = $(this).parent();
		const isCurrentlyOpen = currentItem.hasClass("sp-show");

		// 关闭所有其他项
		$(".sp-item").removeClass("sp-show");

		if (!isCurrentlyOpen) {
			// 如果当前项是关闭的,展开它
			currentItem.addClass("sp-show");
		}
	});

	// 点击内容区域 - 关闭当前项并展开下一个
	$(".sp-text").click(function (e) {
		e.stopPropagation(); // 阻止事件冒泡
		const currentItem = $(this).parent();
		const nextItem = currentItem.next(".sp-item");

		// 关闭当前项
		currentItem.removeClass("sp-show");

		// 展开下一个项（如果存在）
		if (nextItem.length > 0) {
			nextItem.addClass("sp-show");
		} else {
			// 如果没有下一项，展开第一项（循环）
			currentItem.siblings(".sp-item").first().addClass("sp-show");
		}
	});

	// 文本渐出动画
	const graduallyList2 = document.getElementsByClassName("sp-fade-box");
	for (let gradually2 of graduallyList2) {
		console.log("gradually2", gradually2);
		const paragraph2 = gradually2.querySelectorAll(".sp-ani");

		const observerAni = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						paragraph2.forEach((span, index) => {
							span.style.animation = `sp-fade-in 1s ease forwards`;
							span.style.animationDelay = `${index * 0.5 + 0.5}s`;
						});

						observerAni.unobserve(gradually2);
					}
				});
			},
			{
				threshold: 0.3,
			}
		);
		observerAni.observe(gradually2);
	}
	// 文本渐出动画
	const graduallyList3 = document.getElementsByClassName("sp-fade-box2");
	for (let gradually3 of graduallyList3) {
		const paragraph3 = gradually3.querySelectorAll(".sp-ani2");

		const observerAni = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						paragraph3.forEach((span, index) => {
							span.style.animation = `sp-fade-left 1s ease forwards`;
							span.style.animationDelay = `${index * 0.3 + 1}s`;
						});

						observerAni.unobserve(gradually3);
					}
				});
			},
			{
				threshold: 0.3,
			}
		);
		observerAni.observe(gradually3);
	}

	// sp-line-step 进度条拖动功能（支持多个实例）
	const stepLines = document.querySelectorAll(".sp-line-step");

	stepLines.forEach((stepLine) => {
		const stepProgress = stepLine.querySelector(".sp-line-step-progress");
		const stepHandle = stepLine.querySelector(".sp-line-step-handle");

		if (!stepProgress || !stepHandle) return;

		let isStepDragging = false;

		function updateStepProgress(clientX) {
			const rect = stepLine.getBoundingClientRect();
			let percentage = ((clientX - rect.left) / rect.width) * 100;

			// 限制在 0-100% 之间
			percentage = Math.max(0, Math.min(100, percentage));

			// 更新进度条和手柄位置
			stepProgress.style.width = percentage + "%";
			stepHandle.style.left = percentage + "%";
		}

		// 鼠标事件
		stepHandle.addEventListener("mousedown", (e) => {
			isStepDragging = true;
			e.preventDefault();
		});

		stepLine.addEventListener("click", (e) => {
			if (!isStepDragging) {
				updateStepProgress(e.clientX);
			}
		});

		const handleMouseMove = (e) => {
			if (isStepDragging) {
				updateStepProgress(e.clientX);
			}
		};

		const handleMouseUp = () => {
			isStepDragging = false;
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);

		// 触摸事件（移动端）
		stepHandle.addEventListener("touchstart", (e) => {
			isStepDragging = true;
			e.preventDefault();
		});

		stepLine.addEventListener("touchstart", (e) => {
			if (e.target === stepLine) {
				const touch = e.touches[0];
				updateStepProgress(touch.clientX);
			}
		});

		const handleTouchMove = (e) => {
			if (isStepDragging) {
				const touch = e.touches[0];
				updateStepProgress(touch.clientX);
			}
		};

		const handleTouchEnd = () => {
			isStepDragging = false;
		};

		document.addEventListener("touchmove", handleTouchMove, { passive: false });
		document.addEventListener("touchend", handleTouchEnd);
	});
});
